import { List, Map } from 'immutable';

import { makeOperation, makeOperationValue } from '../inventory';
import { updateProductVariationsErrors } from '../product/errors';
import { shapeVariationsForAPI } from '../variations/shapeForAPI';
import { reduceErrors } from '../errors';
import { getSize } from '../iterable/getSize';

import { AMOUNT, EMPTY_VARIATIONS, ERROR, OPERATIONS, VALUE } from '../../constants/product';
import { CATEGORY, PRICE, QUANTITY, SKU, VISIBILITY } from '../../constants/attributes';
import { COMBINATIONS, INFLUENCE, VARIANTS } from '../../constants/variations';
import { ETSY, SHOPIFY } from '../../constants/channels';
import { MAX_QUANTITY } from '../../constants/validations';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';

const { DETAILS, INVENTORY, VARIATIONS } = FORM;

export function editVariations({ channel, product: source, value }) {
  let product = source;
  const operations = [];
  let operation = Map({ type: OPERATIONS[channel].VARIATIONS });
  let confirmation;

  function removeVariationsErrors(errors = EMPTY.MAP) {
    function filterVariationsErrors(_, key) {
      return !/^variations/.test(key);
    }

    return errors.filter(filterVariationsErrors);
  }

  function findVisible(combination) {
    return combination.get(VISIBILITY);
  }

  function reduceCombinationsToQuantity(result, combination, combinationIndex, combinations) {
    const maximum = MAX_QUANTITY[channel] - 1;

    if (result >= maximum) return maximum;

    if (!combination.get(VISIBILITY)) {
      return result
        ? result
        : combinationIndex === getSize(combinations) - 1
          ? Math.min(parseInt(combinations.getIn([0, QUANTITY])) || 0, maximum) || 1
          : result;
    }

    return Math.min((parseInt(combination.get(QUANTITY)) || 0) + result, maximum);
  }

  if (!getSize(value.get(VARIANTS)) && getSize(product.getIn([VARIATIONS, VARIANTS]))) {
    // last variation was deleted
    if (product.has(ERROR)) {
      product = product.update(ERROR, removeVariationsErrors);
    }

    // make inventory operations
    switch (channel) {
      case ETSY: {
        const combinations = product.getIn([VARIATIONS, COMBINATIONS]);
        const influence = product.getIn([VARIATIONS, INFLUENCE]);

        const price = getSize(influence.get(PRICE)) > 0
          ? getSize(combinations)
            ? (combinations.find(findVisible) || combinations.last()).get(PRICE)
            : product.getIn([INVENTORY, PRICE, VALUE])
          : product.getIn([INVENTORY, PRICE, VALUE]);

        const quantity = getSize(influence.get(QUANTITY)) > 0
          ? getSize(combinations)
            ? String(combinations.reduce(reduceCombinationsToQuantity, 0))
            : product.getIn([INVENTORY, QUANTITY, VALUE])
          : product.getIn([INVENTORY, QUANTITY, VALUE]);

        const sku = product.getIn([INVENTORY, SKU, VALUE]);

        product = updateProductVariationsErrors({
          channel,
          product: product
            .setIn([INVENTORY, PRICE, VALUE], price || AMOUNT.ZERO_CENT)
            .setIn([INVENTORY, QUANTITY, VALUE], quantity || AMOUNT.ZERO)
            .setIn([INVENTORY, SKU, VALUE], sku || '')
            .set(VARIATIONS, value),
        });

        if (!product.getIn([INVENTORY, PRICE, ERROR])) {
          const priceOperationValue = makeOperationValue({
            channel,
            overset: false,
            product,
            type: PRICE,
          });

          if (priceOperationValue) {
            const priceOperation = makeOperation({
              channel,
              type: PRICE,
              value: List([priceOperationValue]),
            });

            if (priceOperation) {
              operations.push(priceOperation);
            }
          }
        }

        if (!product.getIn([INVENTORY, QUANTITY, ERROR])) {
          const quantityOperationValue = makeOperationValue({
            channel,
            overset: false,
            product,
            type: QUANTITY,
          });

          if (quantityOperationValue) {
            const quantityOperation = makeOperation({
              channel,
              type: QUANTITY,
              value: List([quantityOperationValue]),
            });

            if (quantityOperation) {
              operations.push(quantityOperation);
            }
          }
        }

        if (!product.getIn([INVENTORY, SKU, ERROR])) {
          const SKUoperationValue = makeOperationValue({
            channel,
            overset: false,
            product,
            type: SKU,
          });

          if (SKUoperationValue) {
            const SKUoperation = makeOperation({
              channel,
              type: SKU,
              value: List([SKUoperationValue]),
            });

            if (SKUoperation) {
              operations.push(SKUoperation);
            }
          }
        }

        break;
      }

      default: {
        product = updateProductVariationsErrors({
          channel,
          product: product.set(VARIATIONS, value),
        });

        break;
      }
    }

    // make variations operation
    switch (channel) {
      case SHOPIFY: {
        operation = operation.set(VALUE, EMPTY_VARIATIONS[channel]);
        break;
      }

      default: {
        const operationData = shapeVariationsForAPI({
          category: product.getIn([DETAILS, CATEGORY, VALUE]),
          channel,
          profile: value.get('currentProfile'),
          toImmutable: true,
          variations: value,
        });

        operation = operation.set(VALUE, operationData.get(VALUE));
        break;
      }
    }

    operations.unshift(operation);
  } else {
    switch (channel) {
      case ETSY: {
        if (getSize(product.getIn([VARIATIONS, VARIANTS]))) {
          const nextInfluence = value.get(INFLUENCE);
          const currentInfluence = product.getIn([VARIATIONS, INFLUENCE]);

          if (getSize(nextInfluence.get(PRICE)) === 0 && getSize(currentInfluence.get(PRICE)) > 0) {
            const price = value.getIn([COMBINATIONS, 0, PRICE]);
            product = product.setIn([INVENTORY, PRICE, VALUE], price);
          }

          if (getSize(nextInfluence.get(QUANTITY)) === 0 && getSize(currentInfluence.get(QUANTITY)) > 0) {
            const quantity = value.getIn([COMBINATIONS, 0, QUANTITY]);
            product = product.setIn([INVENTORY, QUANTITY, VALUE], quantity);
          }

          if (getSize(nextInfluence.get(SKU)) === 0 && getSize(currentInfluence.get(SKU)) > 0) {
            const sku = value.getIn([COMBINATIONS, 0, SKU]);
            product = product.setIn([INVENTORY, SKU, VALUE], sku);
          }
        }

        break;
      }

      default: {
        break;
      }
    }

    product = updateProductVariationsErrors({ channel, product: product.set(VARIATIONS, value) });

    if (!reduceErrors(product.getIn([VARIATIONS, ERROR]))) {
      const operationData = shapeVariationsForAPI({
        category: product.getIn([DETAILS, CATEGORY, VALUE]),
        channel,
        profile: value.get('currentProfile'),
        toImmutable: true,
        variations: value,
      });

      operation = operation.set(VALUE, operationData.get(VALUE));

      if (operationData.has('templateId')) {
        operation = operation.set('templateId', operationData.get('templateId'));
      }

      if (operationData.has('shopifyMeta')) {
        operation = operation.set('shopifyMeta', operationData.get('shopifyMeta'));
      }

      operations.unshift(operation);
    }
  }

  return { confirmation, product, operations };
}
